import { BrandStatusBadge } from '@/components/BrandChip';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { Brand, BrandReviewStatus } from '@rock/types';
import { BrandReviewStatusSchema } from '@rock/types';
import { type FC, useState } from 'react';
import { BRAND_REVIEW_STATUS_META, BRAND_REVIEW_STATUS_ORDER } from './brandReviewStatus';

interface BrandReviewStatusControlProps {
    brand: Brand;
    onChange: (status: BrandReviewStatus, note?: string) => Promise<void>;
    className?: string;
}

/**
 * Header-level control for the brand's manually-controlled curation status — shown next to the brand
 * name so it stays visible across the Details/Aliases/Models tabs, since curation isn't specific to any
 * one tab. A merged brand's status is the merge's to own (the tombstone's `junk`), so it reads as a
 * "Merged" badge rather than a control.
 */
export const BrandReviewStatusControl: FC<BrandReviewStatusControlProps> = ({ brand, onChange, className }) => {
    const status = brand.reviewStatus;
    const meta = BRAND_REVIEW_STATUS_META[status];
    const [asking, setAsking] = useState(false);

    function handleSelect(next: BrandReviewStatus) {
        if (next === 'needs-attention') {
            setAsking(true);
            return;
        }
        onChange(next).catch(() => {
            // The page already toasted the failure.
        });
    }

    return (
        <div className={cn('flex flex-col items-end gap-1', className)}>
            {brand.mergedInto ? (
                <BrandStatusBadge brand={brand} className="h-9 px-3 text-sm" />
            ) : (
                <Select value={status} onValueChange={(value) => handleSelect(BrandReviewStatusSchema.parse(value))}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue>
                            <span className="flex items-center gap-2">
                                <span className={`h-2 w-2 rounded-full ${meta.dotClassName}`} />
                                {meta.label}
                            </span>
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent position="popper" align="end" className="max-w-sm">
                        {BRAND_REVIEW_STATUS_ORDER.map((value) => {
                            const itemMeta = BRAND_REVIEW_STATUS_META[value];
                            return (
                                <SelectItem key={value} value={value}>
                                    <span className="flex flex-col gap-0.5">
                                        <span className="flex items-center gap-2">
                                            <span className={`h-2 w-2 rounded-full ${itemMeta.dotClassName}`} />
                                            {itemMeta.label}
                                        </span>
                                        <span className="pl-4 text-xs text-muted-foreground">{itemMeta.hint}</span>
                                    </span>
                                </SelectItem>
                            );
                        })}
                    </SelectContent>
                </Select>
            )}
            {brand.statusNote && !brand.mergedInto ? (
                <span className="max-w-[260px] whitespace-pre-wrap break-words text-right text-xs text-muted-foreground">
                    {brand.statusNote}
                </span>
            ) : null}
            {brand.statusChangedAt ? (
                <span className="text-xs text-muted-foreground">
                    Changed {new Date(brand.statusChangedAt).toLocaleDateString()}
                </span>
            ) : null}
            {asking && (
                <StatusNoteDialog
                    open
                    onOpenChange={setAsking}
                    onConfirm={async (note) => {
                        await onChange('needs-attention', note);
                        setAsking(false);
                    }}
                />
            )}
        </div>
    );
};

const StatusNoteDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: (note: string) => Promise<void>;
}> = ({ open, onOpenChange, onConfirm }) => {
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);

    async function confirm() {
        setBusy(true);
        try {
            await onConfirm(note.trim());
        } catch {
            // The page already toasted the failure; keep the note so the admin can retry.
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Needs attention</DialogTitle>
                    <DialogDescription>
                        Say what needs a look. The note stays on the brand until its status changes.
                    </DialogDescription>
                </DialogHeader>
                <Textarea
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Status note, e.g. icon not found"
                />
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button onClick={() => void confirm()} disabled={busy || !note.trim()}>
                        Mark needs attention
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
